<template>
  <div :class="$style.visibility">
    <CountBadge
      :ariaLabel="badgeAriaLabel"
      :class="$style.newCount"
      :count="newFeatureItemCount"
      :data-test-id="$testId('badge')"
    >
      <IconButton
        :class="$style.button"
        data-test-id="HeaderItemChecklist__button"
        hasNoBackground
        icon="checklist"
        text="Checklist"
        type="secondary"
        @click="toggleImplementationChecklist"
      />
    </CountBadge>
  </div>
</template>

<script>
import { CountBadge, IconButton } from '@jumpcloud/ui-components';
import { eventProperties } from 'client/GrowthEngineering/utilities/GrowthConstants';
import { implementationChecklists } from 'client/components/ImplementationChecklist/ImplementationChecklists';
import { mapActions, mapGetters, mapState } from 'vuex';
import DateUtil from 'client/util/DateUtil';

const onboardingModule = 'OnboardingModule';
const orgCollection = 'OrganizationsCollection';

const {
  description,
  text,
} = eventProperties;

export default {
  name: 'HeaderItemChecklist',

  components: {
    CountBadge,
    IconButton,
  },

  computed: {
    ...mapGetters(onboardingModule, {
      checklists: 'getImplementationChecklist',
      implementationChecklistDismissed: 'getImplementationChecklistDismissed',
    }),

    ...mapGetters(orgCollection, {
      orgCreatedDate: 'getOrgCreatedDate',
    }),

    ...mapState(onboardingModule, ['adminGrowthData']),

    badgeAriaLabel() {
      return this.hasUnseenNewFeatures
        ? `Checklist badge shows ${this.newFeatureItemCount} new features.`
        : '';
    },

    hasUnseenNewFeatures() {
      return this.newFeatureItemCount > 0;
    },

    newFeatureItemCount() {
      return this.adminGrowthData.onboardingState.newFeatureItemCount;
    },

    newFeaturesSeen() {
      return this.adminGrowthData.onboardingState.newFeaturesSeen;
    },
  },

  created() {
    this.checkNewFeatures();
  },

  methods: {
    ...mapActions(onboardingModule, [
      'addFeatureToImplementationChecklist',
      'selectFeature',
      'updateImplementationChecklistDismissed',
      'updateNewFeatureItemCount',
    ]),

    checkNewFeatures() {
      let newItemCount = this.newFeatureItemCount;
      Object.entries(implementationChecklists).forEach(([key, checklist]) => {
        const isFeatureFlagEnabled = checklist.featureFlag
          && this.$hasFeatureFlag(checklist.featureFlag);
        const orgCreatedBeforeFeature = checklist.startDate
          && DateUtil.isBeforeDate(this.orgCreatedDate, checklist.startDate);
        const featureInChecklist = this.checklists.some((item) => item.feature === key);

        if (orgCreatedBeforeFeature
          && !featureInChecklist
          && !this.newFeaturesSeen[checklist.feature]
          && (isFeatureFlagEnabled || !checklist.featureFlag)) {
          newItemCount += 1;
          this.updateNewFeatureItemCount(newItemCount);
          this.addFeatureToImplementationChecklist({
            ...checklist,
            autoPopulated: true,
            newFeature: true,
          });
          this.selectFeature(checklist);
        }
      });
    },

    toggleImplementationChecklist() {
      this.updateImplementationChecklistDismissed(!this.implementationChecklistDismissed);
      this.$emit('click', {
        [description]: 'Toggles the implementation checklist sidebar.',
        [text]: 'Checklist',
      });
    },
  },
};
</script>

<style scoped module>
@value (max-width-large) from '~css/breakpoints.css';

.button {
  --icon-button-spacing: var(--jc-spacer-small);
}

.newCount /deep/ > span:last-child {
  margin-left: var(--jc-spacer-small);
}

.newCount:hover /deep/ > span:last-child {
  background: var(--jc-color-success-base);
}

/* Legacy vue-test-utils - class needs to exist outside of @media */
.visibility {
  display: block;
}

/* Visibility based NavPanel collapse for mobile/tablet */
@media (max-width: max-width-large) {
  .visibility {
    display: none;
  }
}
</style>
